// =============================================================================
// Watts and Wealth — CardDef + CardEffect interfaces + Zod schemas
// Mirrors data/cards.schema.json (schemaVersion: 1)
// =============================================================================

import { z } from "zod";
import type {
  AuraScope,
  CardEffectKind,
  CardType,
  DiscardMode,
  DurationMode,
  EffectTarget,
  FacilityCategory,
  FacilityType,
  PendingKind,
  TokenOrEnergyCube,
  TriggerEvent,
} from "./enums.js";

// ---------------------------------------------------------------------------
// Zod enum helpers — derived from const object values
// ---------------------------------------------------------------------------

const cardTypeValues = ["Event", "Policy", "Tech", "Mitigate"] as const;
const effectKindValues = [
  "TokenDelta",
  "EnergyDelta",
  "PollutionDelta",
  "Decommission",
  "Destroy",
  "StealEnergy",
  "StealCard",
  "StealFacility",
  "BuildCostMod",
  "TollMod",
  "Conditional",
  "DiceOutcome",
  "Immunity",
  "CancelDisaster",
  "FacilityTransfer",
  "AuraApply",
  "RngBranch",
  "DrawCard",
  "DiscardHand",
  "Trigger",
  "GiveCard",
  "TurnBlock",
] as const;
const effectTargetValues = [
  "Self",
  "Drawer",
  "OpponentChosen",
  "OpponentRandom",
  "AllOpponents",
  "AllPlayers",
  "NearestPlayer",
  "HighestOutputRandom",
  "ByCategory",
  "TileGroup",
  "Global",
] as const;
const tokenOrEnergyCubeValues = ["Token", "EnergyCube"] as const;
const discardModeValues = ["Instantly", "AfterUse", "AfterBuild", "Whenever", "PassStart"] as const;
const durationModeValues = [
  "Instant",
  "UntilEndOfTurn",
  "UntilEndOfRound",
  "UntilEndOfEra",
  "Permanent",
  "UntilUsed",
  "Turns",
] as const;
const pendingKindValues = [
  "NextBuildFree",
  "NextBuildDiscount",
  "NextSolarFree",
  "NextNuclearDiscount",
  "NextPaidDrawFree",
  "NextBuildCostBump",
  "HalveNextTokenLoss",
] as const;
const auraScopeValues = [
  "Toll",
  "BuildCost",
  "Energy",
  "Pollution",
  "IgnoreEraPollution",
  // P1.8: Petrodollar_Trade_Pact registers a per-seat TradeBonus aura that the
  // web-client `confirmTrade` action reads to add `valueInt` bonus tokens per
  // traded energy. No facility scope — pure per-seat passive.
  "TradeBonus",
] as const;

// ---------------------------------------------------------------------------
// Zod schemas (runtime validation — used at cards.json load + WebSocket boundary)
// ---------------------------------------------------------------------------

// BaseCardEffectSchema — all fields except the self-referential triggerResponse.
// Used to break the recursive type cycle: CardEffectSchema references
// BaseCardEffectSchema inside z.lazy for the triggerResponse array.
const BaseCardEffectSchema = z.object({
  kind: z.enum(effectKindValues),
  target: z.enum(effectTargetValues),
  valueInt: z.number().int().optional(),
  mult: z.number().optional(),
  add: z.number().int().optional(),
  chancePct: z.number().int().min(0).max(100).optional(),
  durationMode: z.enum(durationModeValues).optional(),
  predicate: z.string().nullable().optional(),
  categoryFilter: z.array(z.string()).optional(),
  pendingKind: z.enum(pendingKindValues).optional(),
  auraScope: z.enum(auraScopeValues).optional(),
  /**
   * WEB.3.B — Cluster G: DiceOutcome per-category-or-type d6 success table.
   * Keys are FacilityCategory ("Fossil", "Renewable", ...) OR FacilityType
   * ("Hydro", "Coal", ...). FacilityType wins over FacilityCategory at lookup
   * time (resolver lookup priority). Values are integer thresholds 1–6:
   * roll ≤ threshold → success.
   * Codex WEB.3.B R1 MEDIUM fix: typed key set rejects typos like "Renewables"
   * at schema-validation time instead of silently falling through to no-threshold.
   * Example (Mercenaries / Spy Sabotage II):
   *   { Fossil: 4, Renewable: 3, Hydro: 2, Nuclear: 2, Geothermal: 2, Waste: 2 }
   */
  diceTable: z
    .record(
      z.enum([
        // FacilityCategory
        "Fossil",
        "Renewable",
        "Nuclear",
        "Geothermal",
        "Waste",
        // FacilityType (overrides category for the matching type)
        "Coal",
        "Hydro",
        "Biomass",
        "Oil",
        "OffshoreDrilling",
        "WasteToEnergy",
        "Wind",
        "Solar",
        "FloatingSolar",
        "Tidal",
      ]),
      z.number().int().min(1).max(6),
    )
    .optional(),
  /**
   * WEB.3.D.1 — Cluster L multi-tile RngBranch semantics.
   * When true, the resolver scans ALL facilities matching categoryFilter
   * (sorted by tileIndex ascending for deterministic order) and rolls RNG
   * independently per tile. Applied if ≥1 tile hits; Skipped if 0 hits or
   * no matching facilities. Each hit tile gets its own refund/pollution.
   * Single-tile path (affectsAll absent or false) is unchanged.
   * Zod strict boolean: "affectsAll":"yes" is rejected at validation time.
   */
  affectsAll: z.boolean().optional(),
  /**
   * WEB.3.D.2 — OR-choice player-choice model.
   * When present, this effect belongs to the named branch ("pay", "risk",
   * "decom-all", "destroy", etc.). The card's effects array carries effects
   * across two or more branches; the pipeline skips any effect whose branchId
   * does not match ctx.chosenBranchId.
   *
   * Semantics in runCard:
   *   • If ANY effect has branchId AND ctx.chosenBranchId === undefined
   *     → runCard returns Skipped (player must choose before resolving).
   *   • If ctx.chosenBranchId is set, effects whose branchId matches run;
   *     effects with no branchId always run (unconditional setup effects).
   *   • If no effect has branchId, the card is a legacy card: branchId is
   *     ignored and all effects run unconditionally (zero regression).
   *   • Effects with a branchId that does NOT match chosenBranchId are
   *     counted as "branchSkipped" and do NOT count as Applied/Skipped/Failed.
   *
   * Zod strict string: branchId: 123 is rejected at validation time.
   */
  branchId: z.string().optional(),
  /**
   * WEB.4.B — Immunity resolver.
   * Declares which CardEffectKinds this Immunity effect intercepts.
   * Used by the pipeline's immunity-check step (before resolver dispatch):
   * the pipeline calls consumeIfMatches on PipelineServices.immunity;
   * if matched, returns EffectResult.Intercepted without reaching the resolver.
   *
   * Examples (from card text):
   *   UN Green Pact Support: ["StealFacility", "Destroy", "Decommission", "StealEnergy"]
   *   Enhanced Measures:     ["Destroy", "Decommission"]       (single-tile)
   *   Microgrids:            ["Destroy", "RngBranch"]          (event-category immunity)
   *   Blockchain / Decentralized Grid: ["StealEnergy"]         (theft immunity)
   *
   * Zod typed enum keys: ["BogusKind"] is rejected at parse time.
   */
  immuneAgainstKinds: z.array(z.enum(effectKindValues)).optional(),
  /**
   * WEB.4.C — DrawCard resolver.
   * Specifies the card type(s) to draw from. Values are CardType enum members:
   *   ["Mitigate"] → draw only Mitigate-type cards from the Policy pool.
   *   ["Policy"]   → draw any Policy-type card.
   *   ["Event"]    → draw from Event pool.
   *   absent       → draw from Policy pool (any sub-type).
   * Zod typed enum keys: ["Bogus"] is rejected at parse time.
   * Stored as string array to keep Zod schema simple (same pattern as categoryFilter).
   */
  cardPoolFilter: z.array(z.enum(cardTypeValues)).optional(),
  /**
   * WEB.4.E — Trigger resolver: the TriggerEvent this entry listens for.
   * Required when kind === "Trigger". Zod string enum — typos rejected at parse.
   * Values: "AfterStealEnergyTargetingMe" | "AfterFacilityDestroyedMine" |
   *         "AfterFacilityDecommissionedMine" | "AfterTollPaidByOpponentToMe"
   */
  triggerOn: z
    .enum([
      "AfterStealEnergyTargetingMe",
      "AfterFacilityDestroyedMine",
      "AfterFacilityDecommissionedMine",
      "AfterTollPaidByOpponentToMe",
    ] as const satisfies ReadonlyArray<TriggerEvent>)
    .optional(),
  /**
   * WEB.4.E — Trigger resolver: the effects to run when the trigger fires.
   * Required when kind === "Trigger". Each element is a CardEffect.
   * Trigger response effects run at triggerDepth=1 (recursion guard: triggers
   * fired by a trigger response are skipped at the pipeline level).
   *
   * Important: triggerResponse effects are resolved with:
   *   ctx.sourceSeat = ownerSeat (the trigger owner retaliates)
   *   ctx.victimSeat = originalSourceSeat (the attacker becomes victim)
   *
   * Schema note: z.lazy (forward reference) is used here to avoid the
   * TypeScript recursive type inference error. The type annotation on
   * CardEffectSchema below provides the explicit return type so TS can
   * resolve the cycle.
   */
  triggerResponse: z.array(z.lazy(() => CardEffectSchema)).optional(),
});

// CardEffectSchema extends BaseCardEffectSchema — identical at runtime, but
// the explicit z.ZodType<CardEffect> annotation allows z.lazy() above to
// forward-reference CardEffectSchema without TypeScript's implicit-any cycle error.
export const CardEffectSchema: z.ZodType<CardEffect> =
  BaseCardEffectSchema as z.ZodType<CardEffect>;

export const CardDefSchema = z.object({
  name: z.string(),
  cardName: z.string(),
  description: z.string().nullable().optional(),
  artwork: z.string().nullable().optional(),
  cardType: z.enum(cardTypeValues),
  // cardPool intentionally omitted — pool is always derived from cardType (M1 fix).
  era: z.number().int().min(1).max(4),
  buyCost: z.number().int().min(0),
  costCurrency: z.enum(tokenOrEnergyCubeValues),
  discardMode: z.enum(discardModeValues),
  isImmediate: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  educationalFact: z.string().nullable().optional(),
  historicalNote: z.string().nullable().optional(),
  _sourceHash: z.string().nullable().optional(),
  effects: z.array(CardEffectSchema),
});

export const CardsExportSchema = z.object({
  _meta: z.object({
    exportedAt: z.string(),
    unityVersion: z.string(),
    sourceTag: z.string(),
    cardCount: z.number().int(),
    schemaVersion: z.literal(1),
  }),
  cards: z.array(CardDefSchema),
});

// ---------------------------------------------------------------------------
// TypeScript interfaces (derived from schemas — types match enums.ts)
// ---------------------------------------------------------------------------

export interface CardEffect {
  kind: CardEffectKind;
  target: EffectTarget;
  valueInt?: number;
  mult?: number;
  add?: number;
  chancePct?: number;
  durationMode?: DurationMode;
  predicate?: string | null;
  categoryFilter?: string[];
  pendingKind?: PendingKind;
  auraScope?: AuraScope;
  /**
   * WEB.3.B — Cluster G: DiceOutcome per-category-or-type d6 success table.
   * Keys are FacilityCategory OR FacilityType (FacilityType overrides Category).
   * Values are integer thresholds 1–6: roll ≤ threshold → success.
   */
  diceTable?: Partial<Record<FacilityCategory | FacilityType, number>>;
  /**
   * WEB.3.D.1 — Cluster L multi-tile RngBranch semantics.
   * When true, the resolver scans ALL facilities matching categoryFilter
   * (sorted by tileIndex ascending for deterministic order) and rolls RNG
   * independently per tile. Applied if ≥1 tile hits; Skipped if 0 hits or
   * no matching facilities. Each hit tile gets its own refund/pollution.
   */
  affectsAll?: boolean;
  /**
   * WEB.3.D.2 — OR-choice player-choice model.
   * When set, this effect belongs to the named branch. The runCard pipeline
   * checks ctx.chosenBranchId to decide which branch to execute. See
   * CardEffectSchema.branchId for full semantics.
   */
  branchId?: string;
  /**
   * WEB.4.B — Immunity resolver.
   * Declares which CardEffectKinds this Immunity effect intercepts.
   * See CardEffectSchema.immuneAgainstKinds for full semantics.
   */
  immuneAgainstKinds?: CardEffectKind[];
  /**
   * WEB.4.C — DrawCard resolver.
   * Specifies the card type(s) to draw from. Values are CardType enum members.
   * See CardEffectSchema.cardPoolFilter for full semantics.
   */
  cardPoolFilter?: CardType[];
  /**
   * WEB.4.E — Trigger resolver: the TriggerEvent this entry listens for.
   * Required when kind === "Trigger". See CardEffectSchema.triggerOn.
   */
  triggerOn?: TriggerEvent;
  /**
   * WEB.4.E — Trigger resolver: the effects to run when the trigger fires.
   * Required when kind === "Trigger". See CardEffectSchema.triggerResponse.
   */
  triggerResponse?: CardEffect[];
}

export interface CardDef {
  name: string;
  cardName: string;
  description?: string | null;
  artwork?: string | null;
  cardType: CardType;
  // cardPool intentionally omitted — pool is always derived from cardType (M1 fix).
  era: number;
  buyCost: number;
  costCurrency: TokenOrEnergyCube;
  discardMode: DiscardMode;
  isImmediate?: boolean;
  tags?: string[];
  educationalFact?: string | null;
  historicalNote?: string | null;
  _sourceHash?: string | null;
  effects: CardEffect[];
}

export interface CardsExport {
  _meta: {
    exportedAt: string;
    unityVersion: string;
    sourceTag: string;
    cardCount: number;
    schemaVersion: 1;
  };
  cards: CardDef[];
}
